import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api } from '../api/client'
import { translateFintabloError } from '../fintabloErrors'
import { ROUTES } from '../routes'
import Button from './Button'
import Input from './Input'
import IntegrationEventList from './IntegrationEventList'
import Select from './Select'
import { Card, InlineAlert, Skeleton, Status } from './ui'
import {
  CONNECTOR_NAMES,
  SOURCE_FIELD_LABELS,
  ScenarioDropdown,
  ScenarioFilterBody,
  ScenarioMappingBody,
  TRACKABLE_SYSTEM_TARGETS,
  exactCounterpartyCondition,
  loadFilterDirectories,
  mappingOptions,
  normalizeFintabloStatuses,
  syncCrmStages,
  useFilterSources,
  useSavedProviderOptions,
  validFilter
} from './ScenarioEditorParts'
import {
  exportStatus,
  formatNumber,
  periodLabel,
  plural
} from './historyExportFormat'
import directionIcon from '../assets/figma/direction.svg'
import '../styles/connector-hub.css'
import '../styles/scenario-editor.css'
import '../styles/history-export.css'

// Экран выгрузки сделок за прошлые периоды (history-export, ADR R20). Форма —
// шаги редактора сценария: «Период» вместо триггера, «Фильтр», «Действие»,
// «Маппинг полей» и «Проверка и запуск». Запущенная выгрузка — экран хода,
// завершённая — карточка с итогом и тумблерами «Отслеживать».

const FORM_STATUSES = new Set(['draft', 'preparing', 'ready', 'failed'])
const RUN_STATUSES = new Set(['running', 'stopping'])
const POLL_MS = 3000

const TRIAL_RESULTS = {
  created: 'Создана',
  linked: 'Уже была в Финтабло',
  skipped: 'Пропущена',
  failed: 'Ошибка',
  ambiguous: 'Неясно'
}

const FAILURE_REASONS = {
  connection_not_active: 'Подключение к источнику неактивно. Восстановите его и проверьте выгрузку снова.',
  provider_request_failed: 'Источник не ответил. Проверьте выгрузку ещё раз позже.',
  history_export_step_failed: 'Источник не ответил. Проверьте выгрузку ещё раз позже.'
}

const TRACKING_CONFIRM = 'Сделки, которые выгрузка уже создала, получат текущее значение поля из CRM при следующем изменении сделки.'
const TRACKING_NOTE = 'Выключите тумблер, если планируете вносить исправления в Финтабло вручную: иначе правку перезапишет следующее изменение в'

function toExportForm (settings = {}, fields = []) {
  const filters = settings.filters || {}
  return {
    period_from: settings.period_from || '',
    period_to: settings.period_to || '',
    filters: {
      version: 1,
      mode: filters.mode === 'filter' ? 'filter' : 'none',
      groups: Array.isArray(filters.groups)
        ? filters.groups.map(group => Array.isArray(group)
          ? group.map(condition => exactCounterpartyCondition(condition, fields))
          : group)
        : []
    },
    action: {
      type: 'create_deal',
      target_status: settings.action?.target_status ? String(settings.action.target_status) : null,
      tracked_system_targets: settings.action?.tracked_system_targets || []
    },
    rules: (settings.rules || []).map(rule => ({
      type: 'field_mapping',
      active: rule.active !== false,
      config: rule.config || {}
    })),
    create_partners: settings.create_partners !== false
  }
}

// Тело запроса: пустые строки сопоставления не уходят, «Отслеживаемого поля»
// у выгрузки нет.
function settingsPayload (form) {
  return {
    period_from: form.period_from || null,
    period_to: form.period_to || null,
    filters: { version: 1, mode: form.filters.mode, groups: form.filters.groups },
    action: { ...form.action },
    rules: form.rules.filter(rule =>
      String(rule.config?.source || '').trim() || String(rule.config?.target || '').trim()),
    create_partners: form.create_partners
  }
}

function periodErrorOf (form) {
  if (form.period_from && form.period_to && form.period_from > form.period_to) return 'Конец периода раньше начала'
  return null
}

function periodValidFor (form) {
  return Boolean(form.period_from && form.period_to && !periodErrorOf(form))
}

function filterValidFor (form) {
  return periodValidFor(form) && validFilter(form.filters)
}

function actionValidFor (form) {
  return filterValidFor(form) && Boolean(form.action.target_status)
}

function initialStep (form) {
  if (actionValidFor(form)) return 4
  if (filterValidFor(form)) return 3
  if (periodValidFor(form)) return 2
  return 1
}

function etaLabel (seconds) {
  if (seconds === null || seconds === undefined) return 'оцениваем время…'
  const minutes = Math.max(1, Math.round(seconds / 60))
  return `осталось примерно ${minutes} ${plural(minutes, 'минута', 'минуты', 'минут')}`
}

function addYears (value, years) {
  const date = value ? new Date(value) : null
  if (!date || Number.isNaN(date.getTime())) return null
  date.setFullYear(date.getFullYear() + years)
  return date.toLocaleDateString('ru-RU')
}

// Плашка с `to` — ссылка: ошибки и неоднозначные ведут в журнал выгрузки,
// отобранный по ошибкам (приёмка 2026-10-14).
function Tiles ({ items, label }) {
  return (
    <div className='history-export__counts' aria-label={label} role='group'>
      {items.filter(Boolean).map(item => item.to && item.value > 0
        ? (
          <Link
            aria-label={`${formatNumber(item.value)} ${item.label} — открыть в журнале`}
            className='history-export__count history-export__count--link'
            key={item.label}
            to={item.to}
          >
            <b>{formatNumber(item.value)}</b>
            <span>{item.label} →</span>
          </Link>
          )
        : (
          <div className='history-export__count' key={item.label}>
            <b>{formatNumber(item.value)}</b>
            <span>{item.label}</span>
          </div>
          ))}
    </div>
  )
}

function EventsCard ({ events, exportId, onRetry, slug }) {
  return (
    <Card className='scenario-editor__section scenario-editor__events-section' aria-labelledby='export-events-heading'>
      <div className='scenario-editor__section-heading'>
        <div>
          <h2 id='export-events-heading'>Последние события</h2>
          <p>Только события этой выгрузки.</p>
        </div>
      </div>
      <IntegrationEventList error={events.error} items={events.items} loading={events.loading} onRetry={onRetry} />
      <Link className='connector-hub__events-link' to={`${ROUTES.LOGS(slug)}?export_id=${exportId}`}>Все события выгрузки</Link>
    </Card>
  )
}

// Подписи условий — как в `runtime/scenario-filters.js`; нужны, когда поля
// источника не загрузились и подписи из них взять нельзя.
const OPERATOR_LABELS = {
  eq: 'равно',
  neq: 'не равно',
  contains: 'содержит',
  not_contains: 'не содержит',
  gt: 'больше',
  gte: 'больше или равно',
  lt: 'меньше',
  lte: 'меньше или равно',
  after: 'позднее',
  before: 'раньше',
  between: 'между',
  includes: 'содержит значение',
  not_includes: 'не содержит значение',
  empty: 'не заполнено',
  not_empty: 'заполнено'
}

// Строка условия фильтра словами — для карточки выгрузки.
function filterSummary (filters, liveFields, statuses) {
  if (filters?.mode !== 'filter' || !(filters.groups || []).length) return 'Без фильтров'
  const conditionText = condition => {
    const field = liveFields.find(item => item.key === condition.field)
    const fieldLabel = SOURCE_FIELD_LABELS[condition.field] || field?.label || condition.field
    const operator = (field?.operators || []).find(item => item.value === condition.operator)?.label ||
      OPERATOR_LABELS[condition.operator] || condition.operator
    const options = [...(field?.options || []), ...(field?.options_source === 'statuses' ? statuses : [])]
    const value = options.find(option => String(option.value) === String(condition.value))?.label || condition.value
    return value === undefined || value === '' ? `${fieldLabel} ${operator}` : `${fieldLabel} ${operator} «${value}»`
  }
  return filters.groups.map(group => group.map(conditionText).join(' и ')).join(' или ')
}

export default function HistoryExportEditor ({ user }) {
  const { slug, exportId } = useParams()
  const navigate = useNavigate()
  const [exp, setExp] = useState(null)
  const [form, setForm] = useState(null)
  const [fintabloStatuses, setFintabloStatuses] = useState([])
  const [scenarioFields, setScenarioFields] = useState({})
  const [liveFields, setLiveFields] = useState([])
  const [validation, setValidation] = useState({ valid: null, errors: [], warnings: [] })
  const [error, setError] = useState(null)
  const [actionError, setActionError] = useState(null)
  const [notice, setNotice] = useState(null)
  const [degraded, setDegraded] = useState([])
  const [stageSyncWarning, setStageSyncWarning] = useState(null)
  const [busy, setBusy] = useState(null)
  const [activeStep, setActiveStep] = useState(1)
  const [events, setEvents] = useState({ loading: true, items: [], error: null })
  const [copyState, setCopyState] = useState({ open: false, scenarios: [], scenarioId: '', error: null, loading: false })
  const [confirmStop, setConfirmStop] = useState(false)
  // Пустой период — не ошибка, пока пользователь не попробовал проверить.
  const [attempted, setAttempted] = useState(false)
  const savedSnapshot = useRef('')
  const requestId = useRef(0)
  const busyRef = useRef(false)
  const sources = useFilterSources(slug)
  const {
    providerOptions,
    sourceEmployees,
    loadProviderOptions,
    searchCounterparties,
    counterpartyChoices,
    reset: resetSources,
    apply: applySources
  } = sources
  const crmName = CONNECTOR_NAMES[slug] || slug

  const applyExport = useCallback((result, fields) => {
    const nextForm = toExportForm(result.settings, fields)
    setExp(result)
    setForm(nextForm)
    savedSnapshot.current = JSON.stringify(nextForm)
    setValidation(result.validation || { valid: null, errors: [], warnings: [] })
    return nextForm
  }, [])

  const loadExport = useCallback(async () => {
    const currentRequest = ++requestId.current
    setExp(null)
    setForm(null)
    setError(null)
    setDegraded([])
    setStageSyncWarning(null)
    resetSources()
    try {
      const failures = []
      const optional = (promise, label, fallback, describe) => promise.catch(err => {
        failures.push({ label, reason: describe ? describe(err) : null })
        return fallback
      })
      const warning = await syncCrmStages(slug, user?.id)
      const [result, statuses, fields, live] = await Promise.all([
        api.getExport(exportId),
        user?.id
          ? optional(api.getFintabloStatuses(user.id), 'Статусы Финтабло', { statuses: [] }, err => translateFintabloError(err?.message))
          : Promise.resolve({ statuses: [] }),
        optional(api.getScenarioFields(slug), 'Поля сценария', {}),
        optional(api.getLiveFields(slug), 'Поля источника', { fields: [] })
      ])
      const directories = result.connection_id
        ? await loadFilterDirectories(slug, result.connection_id, result.settings?.filters, live.fields || [], optional)
        : null
      if (currentRequest !== requestId.current) return
      const nextForm = applyExport(result, live.fields || [])
      setDegraded(failures)
      setStageSyncWarning(warning)
      setFintabloStatuses(normalizeFintabloStatuses(statuses))
      setScenarioFields(fields)
      setLiveFields(live.fields || [])
      applySources(result.connection_id, directories)
      setActiveStep(initialStep(nextForm))
    } catch (err) {
      if (currentRequest === requestId.current) setError(err.message)
    }
  }, [applyExport, applySources, exportId, resetSources, slug, user?.id])

  useEffect(() => {
    loadExport()
    return () => { requestId.current += 1 }
  }, [loadExport])

  const status = exp?.status
  const inForm = Boolean(exp && FORM_STATUSES.has(status) && !exp.started_at)
  const inRun = Boolean(exp && RUN_STATUSES.has(status))
  const inCard = Boolean(exp && !inForm && !inRun)

  const loadEvents = useCallback(() => {
    api.getIntegrationEvents({ export_id: Number(exportId), limit: 5 })
      .then(result => setEvents({ loading: false, items: result.items || [], error: null }))
      .catch(err => setEvents({ loading: false, items: [], error: err.message }))
  }, [exportId])

  useEffect(() => {
    if (inRun || inCard) loadEvents()
  }, [inCard, inRun, loadEvents])

  // Подготовка и выгрузка идут на сервере: экран спрашивает ход сам.
  useEffect(() => {
    if (!exp || !['preparing', 'running', 'stopping'].includes(exp.status)) return undefined
    const timer = window.setInterval(() => {
      api.getExport(exportId)
        .then(result => {
          setExp(result)
          if (RUN_STATUSES.has(result.status)) loadEvents()
        })
        .catch(() => {})
    }, POLL_MS)
    return () => window.clearInterval(timer)
  }, [exp, exportId, loadEvents])

  const dirty = form ? JSON.stringify(form) !== savedSnapshot.current : false

  useEffect(() => {
    if (!dirty || !form || !inForm) return undefined
    const timer = window.setTimeout(() => {
      api.validateExport(exportId, settingsPayload(form)).then(setValidation).catch(() => {})
    }, 300)
    return () => window.clearTimeout(timer)
  }, [dirty, exportId, form, inForm])

  useEffect(() => {
    if (!dirty) return undefined
    const beforeUnload = event => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', beforeUnload)
    return () => window.removeEventListener('beforeunload', beforeUnload)
  }, [dirty])

  useSavedProviderOptions(form?.filters, liveFields, loadProviderOptions)

  function changeForm (updater) {
    setNotice(null)
    setActionError(null)
    setForm(current => updater(current))
  }

  async function runCommand (name, command) {
    if (busyRef.current) return
    busyRef.current = true
    setBusy(name)
    setActionError(null)
    setNotice(null)
    try {
      await command()
    } catch (err) {
      setActionError(err.message)
      if (err.details) setValidation(current => ({ ...current, valid: false, errors: err.details }))
    } finally {
      busyRef.current = false
      setBusy(null)
    }
  }

  async function saveForm () {
    const result = await api.saveExport(Number(exportId), settingsPayload(form))
    applyExport(result, liveFields)
    return result
  }

  const save = () => runCommand('save', async () => {
    await saveForm()
    setNotice('Черновик сохранён')
  })

  // «Проверить»: сохранить и, если список сделок нужно собрать заново, собрать.
  const check = () => runCommand('check', async () => {
    setAttempted(true)
    let result = dirty ? await saveForm() : exp
    if (['draft', 'failed'].includes(result.status)) {
      result = await api.prepareExport(Number(exportId))
      setExp(result)
    }
  })

  const trial = () => runCommand('trial', async () => {
    setExp(await api.trialExport(Number(exportId)))
  })

  const start = () => runCommand('start', async () => {
    setExp(await api.startExport(Number(exportId)))
  })

  const stop = () => runCommand('stop', async () => {
    setConfirmStop(false)
    setExp(await api.stopExport(Number(exportId)))
  })

  // «Довыгрузить остальные»: новая выгрузка с теми же настройками с первой
  // невыгруженной сделки (приёмка 2026-10-14).
  const continueRest = () => runCommand('continue', async () => {
    const result = await api.continueExport(Number(exportId))
    navigate(ROUTES.EXPORT(slug, result.export.id))
  })

  const remove = () => {
    if (!window.confirm('Удалить черновик выгрузки?')) return
    runCommand('delete', async () => {
      await api.deleteExport(Number(exportId))
      navigate(ROUTES.INTEGRATION(slug))
    })
  }

  function leave (event) {
    if (dirty && !window.confirm('Есть несохранённые изменения. Выйти без сохранения?')) event.preventDefault()
  }

  function setPeriod (key, value) {
    setActiveStep(current => Math.min(current, value ? current : 1))
    changeForm(current => ({ ...current, [key]: value }))
  }

  function setFilterMode (mode) {
    setActiveStep(current => Math.min(current, 2))
    changeForm(current => ({
      ...current,
      filters: {
        ...current.filters,
        mode,
        groups: mode === 'filter' && current.filters.groups.length === 0
          ? [[{ field: '', field_type: 'string', operator: '', value: '' }]]
          : mode === 'none' ? [] : current.filters.groups
      }
    }))
  }

  function updateFilterCondition (groupIndex, conditionIndex, patch) {
    setActiveStep(current => Math.min(current, 2))
    changeForm(current => ({
      ...current,
      filters: {
        ...current.filters,
        groups: current.filters.groups.map((group, currentGroupIndex) =>
          currentGroupIndex === groupIndex
            ? group.map((condition, currentConditionIndex) =>
              currentConditionIndex === conditionIndex ? { ...condition, ...patch } : condition)
            : group)
      }
    }))
  }

  function addAndCondition () {
    setActiveStep(current => Math.min(current, 2))
    changeForm(current => {
      const groups = current.filters.groups.length
        ? current.filters.groups.map((group, index) => index === current.filters.groups.length - 1
          ? [...group, { field: '', field_type: 'string', operator: '', value: '' }]
          : group)
        : [[{ field: '', field_type: 'string', operator: '', value: '' }]]
      return { ...current, filters: { ...current.filters, mode: 'filter', groups } }
    })
  }

  function addOrCondition () {
    setActiveStep(current => Math.min(current, 2))
    changeForm(current => ({
      ...current,
      filters: {
        ...current.filters,
        mode: 'filter',
        groups: [...current.filters.groups, [{ field: '', field_type: 'string', operator: '', value: '' }]]
      }
    }))
  }

  function removeFilterCondition (groupIndex, conditionIndex) {
    setActiveStep(current => Math.min(current, 2))
    changeForm(current => {
      const groups = current.filters.groups
        .map((group, currentGroupIndex) => currentGroupIndex === groupIndex
          ? group.filter((_, currentConditionIndex) => currentConditionIndex !== conditionIndex)
          : group)
        .filter(group => group.length > 0)
      return { ...current, filters: { ...current.filters, mode: groups.length ? 'filter' : 'none', groups } }
    })
  }

  function addFieldMapping () {
    changeForm(current => ({
      ...current,
      rules: [...current.rules, { type: 'field_mapping', active: true, config: { source: '', target: '' } }]
    }))
  }

  function updateRule (index, updater) {
    changeForm(current => ({
      ...current,
      rules: current.rules.map((rule, ruleIndex) => ruleIndex === index ? updater(rule) : rule)
    }))
  }

  function removeRule (index) {
    changeForm(current => ({ ...current, rules: current.rules.filter((_, ruleIndex) => ruleIndex !== index) }))
  }

  // Тумблеры формы — правка настроек; в карточке — сразу на сервер.
  function saveTracking (nextForm) {
    runCommand('tracking', async () => {
      const result = await api.saveExportTracking(Number(exportId), {
        tracked_system_targets: nextForm.action.tracked_system_targets || [],
        rules: nextForm.rules.map(rule => ({ tracked: rule.config?.tracked === true }))
      })
      applyExport(result, liveFields)
      setNotice('Сохранено')
    })
  }

  function toggleRuleTracking (index, next) {
    if (inCard) {
      if (next && !window.confirm(TRACKING_CONFIRM)) return
      saveTracking({
        ...form,
        rules: form.rules.map((rule, ruleIndex) => ruleIndex === index ? { ...rule, config: { ...rule.config, tracked: next } } : rule)
      })
      return
    }
    updateRule(index, current => ({ ...current, config: { ...current.config, tracked: next } }))
  }

  function toggleSystemTracking (target, next) {
    const targets = (form.action.tracked_system_targets || []).filter(item => item !== target)
    const action = { ...form.action, tracked_system_targets: next ? [...targets, target] : targets }
    if (inCard) {
      if (next && !window.confirm(TRACKING_CONFIRM)) return
      saveTracking({ ...form, action })
      return
    }
    changeForm(current => ({ ...current, action }))
  }

  async function openCopy () {
    setCopyState(current => ({ ...current, open: true, loading: true, error: null }))
    try {
      const hub = await api.getConnectorHub(slug)
      setCopyState(current => ({ ...current, loading: false, scenarios: hub.scenarios || [] }))
    } catch (err) {
      setCopyState(current => ({ ...current, loading: false, error: err.message }))
    }
  }

  // Копия на момент нажатия: сопоставления и тумблеры, без фильтра, стадии
  // и «Отслеживаемого поля» сценария (решение владельца 2026-10-13).
  async function copyMapping () {
    if (!copyState.scenarioId) return
    setCopyState(current => ({ ...current, loading: true, error: null }))
    try {
      const scenario = await api.getScenarioEditor(copyState.scenarioId)
      changeForm(current => ({
        ...current,
        action: { ...current.action, tracked_system_targets: scenario.action?.tracked_system_targets || [] },
        rules: (scenario.rules || [])
          .filter(rule => (rule.type || rule.rule_type) === 'field_mapping')
          .map(rule => ({ type: 'field_mapping', active: rule.active !== false, config: { ...(rule.config || {}) } }))
      }))
      setCopyState({ open: false, scenarios: copyState.scenarios, scenarioId: '', error: null, loading: false })
    } catch (err) {
      setCopyState(current => ({ ...current, loading: false, error: err.message }))
    }
  }

  if (error && !exp) {
    return (
      <div className='scenario-editor'>
        <Link className='connector-hub__back' to={ROUTES.INTEGRATION(slug)}>К хабу {crmName}</Link>
        <InlineAlert title='Не удалось загрузить выгрузку' tone='error'>
          <span>{error}</span>
          <button className='ui-button ui-button--ghost connector-hub__retry' type='button' onClick={loadExport}>Повторить</button>
        </InlineAlert>
      </div>
    )
  }
  if (!exp || !form) return <Skeleton className='connector-hub__header-skeleton' aria-label='Загрузка выгрузки' />

  const statusView = exportStatus(exp.status)
  const errorsLink = `${ROUTES.LOGS(slug)}?export_id=${exportId}&status=error`
  const fieldOptions = mappingOptions(scenarioFields, liveFields, 'create_deal')
  const filterFields = liveFields.filter(field => field.filterable !== false)
  const systemRows = [...fieldOptions.systemMappings].sort((a, b) =>
    Number(TRACKABLE_SYSTEM_TARGETS.has(a.targetField)) - Number(TRACKABLE_SYSTEM_TARGETS.has(b.targetField)))
  const systemMappingKeys = new Set(fieldOptions.systemMappings.map(mapping => `${mapping.source}:${mapping.targetField}`))
  const fieldMappingRules = form.rules
    .map((rule, index) => ({ rule, index }))
    .filter(item => !systemMappingKeys.has(`${item.rule.config?.source}:${item.rule.config?.target}`))
  const ruleErrors = (validation.errors || []).filter(issue => issue.field === 'rules')
  const ruleWarnings = (validation.warnings || []).filter(issue => issue.field === 'rules')
  const statuses = exp.filter_options?.statuses || []
  const stageLabel = fintabloStatuses.find(option => option.value === String(form.action.target_status))?.label || form.action.target_status || '—'

  const header = title => (
    <header className='scenario-editor__header'>
      <div className='scenario-editor__title-group'>
        <h1 className='scenario-editor__name-text'>{title}</h1>
        <div className='scenario-editor__meta'>
          <span className='scenario-editor__system-chip'>{crmName}</span>
          <img className='scenario-editor__direction-icon' src={directionIcon} alt='' />
          <span className='scenario-editor__system-chip'>Финтабло</span>
          {dirty && <span className='scenario-editor__dirty'>Есть несохранённые изменения</span>}
        </div>
      </div>
      <div className='scenario-editor__header-status'>
        <Status tone={statusView.tone}>{statusView.label}</Status>
      </div>
    </header>
  )

  const back = <Link className='connector-hub__back' to={ROUTES.INTEGRATION(slug)} onClick={leave}>К хабу {crmName}</Link>
  const feedback = (
    <>
      {actionError && <InlineAlert title='Не удалось выполнить действие' tone='error'>{actionError}</InlineAlert>}
      {notice && <p className='scenario-editor__action-feedback' role='status'>{notice}</p>}
    </>
  )

  if (inRun) {
    const progress = exp.progress || {}
    const percent = progress.total ? Math.round(100 * progress.processed / progress.total) : 0
    return (
      <div className='scenario-editor'>
        {back}
        {header(`Выгрузка ${periodLabel(form.period_from, form.period_to)}`)}
        <Card className='scenario-editor__section' aria-labelledby='export-run-heading'>
          <div className='scenario-editor__section-heading'><h2 id='export-run-heading'>Ход выгрузки</h2></div>
          <div className='scenario-editor__section-body'>
            <div className='history-export__progress-line'>
              <strong>Отправлено {formatNumber(progress.processed)} из {formatNumber(progress.total)} · порция {progress.batch} из {progress.batches}</strong>
              <span>{exp.status === 'stopping' ? 'останавливается…' : etaLabel(progress.eta_seconds)}</span>
            </div>
            <div
              aria-label='Отправлено сделок'
              aria-valuemax={progress.total || 0}
              aria-valuemin={0}
              aria-valuenow={progress.processed || 0}
              className='history-export__bar'
              role='progressbar'
            >
              <span style={{ width: `${percent}%` }} />
            </div>
            <Tiles
              label='Счётчики выгрузки'
              items={[
                { label: 'создано', value: exp.counts.created },
                { label: 'уже в Финтабло', value: exp.counts.linked },
                { label: 'с ошибкой', value: exp.counts.failed + exp.counts.ambiguous, to: errorsLink },
                { label: 'контрагентов создано', value: exp.counts.partners_created }
              ]}
            />
            {exp.status === 'stopping'
              ? (
                <div className='history-export__notice'>
                  <InlineAlert title='Выгрузка прерывается' tone='warning'>
                    Останавливается после текущей сделки — это несколько секунд. Созданные сделки останутся в Финтабло.
                  </InlineAlert>
                </div>
                )
              : <InlineAlert tone='info'>Страницу можно закрыть — выгрузка продолжится на сервере. Ход и итог будут здесь и в разделе «Прошлые периоды».</InlineAlert>}
            {exp.status === 'running' && !confirmStop && (
              <div className='history-export__actions'>
                <Button onClick={() => setConfirmStop(true)} variant='stroke'>Прервать выгрузку</Button>
              </div>
            )}
            {exp.status === 'running' && confirmStop && (
              <div className='history-export__danger'>
                <InlineAlert title='Прервать выгрузку?' tone='error'>
                  <span>Выгрузка остановится после текущей сделки. {formatNumber(exp.counts.created)} уже созданных сделок останутся в Финтабло. Продолжить можно новой выгрузкой того же периода — созданные сделки она пропустит.</span>
                  <div className='history-export__actions'>
                    <Button loading={busy === 'stop'} disabled={busy !== null} onClick={stop}>Прервать</Button>
                    <Button onClick={() => setConfirmStop(false)} variant='ghost'>Продолжить выгрузку</Button>
                  </div>
                </InlineAlert>
              </div>
            )}
            {feedback}
          </div>
        </Card>
        <EventsCard events={events} exportId={exportId} onRetry={loadEvents} slug={slug} />
      </div>
    )
  }

  if (inCard) {
    const counts = exp.counts
    const trackingUntil = addYears(exp.started_at, 2)
    return (
      <div className='scenario-editor'>
        {back}
        {header(`Выгрузка ${periodLabel(form.period_from, form.period_to)}`)}
        {exp.error_reason && (
          <InlineAlert title='Выгрузка остановлена' tone='error'>{FAILURE_REASONS[exp.error_reason] || 'Выгрузка остановлена из-за ошибки. Обратитесь в поддержку.'}</InlineAlert>
        )}
        <Card className='scenario-editor__section' aria-labelledby='export-total-heading'>
          <div className='scenario-editor__section-heading'><h2 id='export-total-heading'>Итог</h2></div>
          <div className='scenario-editor__section-body'>
            <Tiles
              label='Итог выгрузки'
              items={[
                { label: 'найдено', value: counts.total },
                { label: 'создано', value: counts.created },
                { label: 'уже в Финтабло', value: counts.linked },
                { label: 'с ошибкой', value: counts.failed, to: errorsLink },
                counts.ambiguous ? { label: 'неоднозначно', value: counts.ambiguous, to: errorsLink } : null,
                counts.skipped ? { label: 'пропущено', value: counts.skipped } : null,
                { label: 'контрагентов создано', value: counts.partners_created }
              ]}
            />
            {exp.remaining && (
              <div className='history-export__remaining'>
                <p>
                  Не выгружено {formatNumber(exp.remaining.count)} {plural(exp.remaining.count, 'сделка', 'сделки', 'сделок')}, созданные {periodLabel(exp.remaining.period_from, exp.remaining.period_to)}.
                  <small>«Довыгрузить остальные» создаст новую выгрузку с теми же настройками за этот период: проверка и пробные сделки — как обычно, уже созданные сделки она пропустит.</small>
                </p>
                <Button loading={busy === 'continue'} disabled={busy !== null} onClick={continueRest}>Довыгрузить остальные</Button>
              </div>
            )}
          </div>
        </Card>
        <Card className='scenario-editor__section' aria-labelledby='export-settings-heading'>
          <div className='scenario-editor__section-heading'><h2 id='export-settings-heading'>Настройки</h2></div>
          <div className='scenario-editor__section-body'>
            <dl className='history-export__settings'>
              <dt>Период</dt><dd>сделки, созданные {periodLabel(form.period_from, form.period_to)}</dd>
              <dt>Фильтр</dt><dd>{filterSummary(form.filters, liveFields, statuses)}</dd>
              <dt>Стадия Финтабло</dt><dd>{stageLabel}</dd>
            </dl>
            <p className='scenario-editor__step-description'>После запуска настройки не меняются.</p>
          </div>
        </Card>
        <Card className='scenario-editor__section' aria-labelledby='export-mapping-heading'>
          <div className='scenario-editor__section-heading'><h2 id='export-mapping-heading'>Маппинг и обновление сделок</h2></div>
          <div className='scenario-editor__section-body'>
            <p className='scenario-editor__step-description'>
              Поля с включённым «Отслеживать» обновляются в Финтабло при изменении в {crmName}{trackingUntil ? ` — до ${trackingUntil}` : ''}. {TRACKING_NOTE} {crmName}.
            </p>
            <ScenarioMappingBody
              actionStepValid
              activeStep={4}
              addFieldMapping={addFieldMapping}
              crmName={crmName}
              fieldMappingRules={fieldMappingRules}
              fieldOptions={fieldOptions}
              form={form}
              isReadOnly
              liveFields={liveFields}
              removeRule={removeRule}
              ruleErrors={[]}
              ruleWarnings={[]}
              slug={slug}
              systemRows={systemRows}
              toggleRuleTracking={toggleRuleTracking}
              toggleSystemTracking={toggleSystemTracking}
              trackingAvailable
              trackingEditable
              updateRule={updateRule}
              validation={{ errors: [], warnings: [] }}
            />
            {feedback}
          </div>
        </Card>
        <EventsCard events={events} exportId={exportId} onRetry={loadEvents} slug={slug} />
      </div>
    )
  }

  // --- Форма ---------------------------------------------------------------
  const isReadOnly = status === 'preparing'
  const periodError = periodErrorOf(form)
  const periodValid = periodValidFor(form)
  const filterValid = filterValidFor(form)
  const actionValid = actionValidFor(form)
  const stepState = step => activeStep > step ? 'complete' : activeStep === step ? 'active' : 'inactive'
  const stepMarker = step => stepState(step) === 'complete'
    ? <span aria-label={`Шаг ${step} завершён`} className='scenario-editor__step-number'>✓</span>
    : <span className='scenario-editor__step-number'>{step}</span>
  const advance = step => {
    setActiveStep(current => Math.max(current, step + 1))
    window.setTimeout(() => document.querySelector(`[data-export-step="${step + 1}"]`)?.focus(), 0)
  }
  const fieldIssue = name => (validation.errors || []).find(issue => issue.field === name)
  // Ошибки пустого периода не считаются, пока не было попытки проверить.
  const shownErrors = (validation.errors || []).filter(issue =>
    attempted || !['period_from_required', 'period_to_required'].includes(issue.code))
  const errorCount = shownErrors.length + (periodError ? 1 : 0)
  const check5 = exp.check
  const createPartners = form.create_partners
  const canCheck = !busy && !isReadOnly && actionValid && (dirty || ['draft', 'failed'].includes(status))
  const canTrial = !busy && !dirty && exp.can_trial
  const canStart = !busy && !dirty && exp.can_start
  const referenceLine = (type, name, without) => {
    const reference = (check5?.references || []).find(item => item.type === type)
    if (!reference) return null
    const ok = reference.unmapped === 0 && reference.total > 0
    const text = reference.total === 0
      ? `${name}: справочник пуст — загрузите его`
      : ok
        ? `${name}: все ${formatNumber(reference.mapped)} сопоставлены`
        : `${name}: ${formatNumber(reference.unmapped)} из ${formatNumber(reference.total)} не сопоставлены — сделки уйдут без ${without}`
    return (
      <li key={type}>
        <div className='history-export__ref-line'>
          <span>{text}</span>
          {ok
            ? <Status tone='success'>Готово</Status>
            : <Link to={ROUTES.REFERENCE(slug, type)}>Открыть справочник</Link>}
        </div>
      </li>
    )
  }
  const unmappedBuyers = check5?.counterparties?.unmapped || 0
  const buyersText = unmappedBuyers === 0
    ? 'Покупатели: все сопоставлены'
    : createPartners
      ? `Покупатели: ${formatNumber(unmappedBuyers)} не сопоставлены — найдутся по названию или будут созданы в Финтабло`
      : `Покупатели: ${formatNumber(unmappedBuyers)} не сопоставлены — сделки уйдут без покупателя`
  const readiness = errorCount
    ? { tone: 'error', text: `⊗ Исправьте ошибки: ${errorCount}` }
    : dirty
      ? { tone: 'warning', text: '△ Изменения не сохранены — нажмите «Проверить»' }
      : status === 'preparing'
        ? { tone: 'warning', text: 'Собираем список сделок…' }
        : status === 'failed'
          ? { tone: 'error', text: '⊗ Не удалось собрать список сделок' }
          : exp.can_start
            ? { tone: 'success', text: '✓ Проба прошла — можно выгружать' }
            : status === 'ready'
              ? { tone: 'success', text: '✓ Готово к пробной отправке' }
              : { tone: 'warning', text: 'Заполните шаги и нажмите «Проверить»' }

  return (
    <div className='scenario-editor'>
      {back}
      {header('Выгрузка за прошлый период')}

      {degraded.length > 0 && (
        <InlineAlert title='Часть данных не загрузилась' tone='warning'>
          <ul className='scenario-editor__degraded'>
            {degraded.map(item => <li key={item.label}>{item.label}{item.reason ? ` — ${item.reason}` : ''}</li>)}
          </ul>
          <span>Списки на этих шагах будут пустыми, пока источник не ответит.</span>
        </InlineAlert>
      )}
      {stageSyncWarning && <InlineAlert title='Не удалось обновить стадии' tone='warning'>{stageSyncWarning}</InlineAlert>}

      <Card className='scenario-editor__section scenario-editor__step' aria-labelledby='export-period-heading' data-export-step='1' data-step-state={stepState(1)} tabIndex={-1}>
        <div className='scenario-editor__section-heading scenario-editor__step-heading'>
          {stepMarker(1)}
          <h2 id='export-period-heading'>Период</h2>
          {stepState(1) === 'complete' && <span className='scenario-editor__step-summary'>{periodLabel(form.period_from, form.period_to)}</span>}
        </div>
        <div className='scenario-editor__section-body'>
          <p className='scenario-editor__step-description'>Сделки, созданные в {crmName}:</p>
          <div className='scenario-editor__step-grid'>
            <label className='scenario-editor__field'>
              <span>с</span>
              <Input aria-label='Период: с' disabled={isReadOnly} type='date' value={form.period_from} onChange={event => setPeriod('period_from', event.target.value)} />
              {attempted && fieldIssue('period_from') && <small className='scenario-editor__field-error'>{fieldIssue('period_from').message}</small>}
            </label>
            <label className='scenario-editor__field'>
              <span>по</span>
              <Input aria-label='Период: по' disabled={isReadOnly} error={Boolean(periodError)} type='date' value={form.period_to} onChange={event => setPeriod('period_to', event.target.value)} />
              {periodError && <small className='scenario-editor__field-error'>{periodError}</small>}
              {!periodError && attempted && fieldIssue('period_to') && <small className='scenario-editor__field-error'>{fieldIssue('period_to').message}</small>}
            </label>
          </div>
          <p className='scenario-editor__step-description'>Сделки, которые уже есть в Финтабло от сценария или прошлой выгрузки, будут пропущены. Границы дня — по московскому времени.</p>
          <div className='scenario-editor__step-footer'>
            <Button disabled={activeStep !== 1 || !periodValid || isReadOnly} onClick={() => advance(1)} variant='stroke'>Продолжить</Button>
          </div>
        </div>
      </Card>

      <Card className='scenario-editor__section scenario-editor__step' aria-labelledby='export-filter-heading' data-export-step='2' data-step-state={stepState(2)} tabIndex={-1}>
        <div className='scenario-editor__section-heading scenario-editor__step-heading'>
          {stepMarker(2)}
          <h2 id='export-filter-heading'>Фильтр</h2>
        </div>
        <div className='scenario-editor__section-body'>
          <ScenarioFilterBody
            activeStep={activeStep}
            addAndCondition={addAndCondition}
            addOrCondition={addOrCondition}
            counterpartyChoices={counterpartyChoices}
            crmName={crmName}
            filterFields={filterFields}
            form={form}
            isReadOnly={isReadOnly}
            liveFields={liveFields}
            loadProviderOptions={loadProviderOptions}
            providerOptions={providerOptions}
            removeFilterCondition={removeFilterCondition}
            searchCounterparties={searchCounterparties}
            setFilterMode={setFilterMode}
            slug={slug}
            sourceEmployees={sourceEmployees}
            statuses={statuses}
            triggerStepValid={periodValid}
            updateFilterCondition={updateFilterCondition}
          />
          <div className='scenario-editor__step-footer'>
            <Button disabled={activeStep !== 2 || !filterValid || isReadOnly} onClick={() => advance(2)} variant='stroke'>Продолжить</Button>
          </div>
        </div>
      </Card>

      <Card className='scenario-editor__section scenario-editor__step' aria-labelledby='export-action-heading' data-export-step='3' data-step-state={stepState(3)} tabIndex={-1}>
        <div className='scenario-editor__section-heading scenario-editor__step-heading'>
          {stepMarker(3)}
          <h2 id='export-action-heading'>Действие</h2>
          {stepState(3) === 'complete' && <span className='scenario-editor__step-summary'>Создать сделку · {stageLabel}</span>}
        </div>
        <div className='scenario-editor__section-body'>
          <div className='scenario-editor__step-grid'>
            <label className='scenario-editor__field'>
              <span>Действие в Финтабло</span>
              <Input aria-label='Действие в Финтабло' disabled readOnly value='Создать сделку' />
            </label>
            <label className='scenario-editor__field'>
              <span>Создать в стадии Финтабло</span>
              <ScenarioDropdown
                aria-label='Создать в стадии Финтабло'
                disabled={activeStep < 3 || !filterValid || isReadOnly}
                error={Boolean(fieldIssue('action.target_status'))}
                options={fintabloStatuses}
                placeholder='Выберите стадию'
                searchable
                value={form.action.target_status || ''}
                onChange={event => {
                  setActiveStep(current => Math.min(current, 3))
                  changeForm(current => ({ ...current, action: { ...current.action, target_status: event.target.value || null } }))
                }}
              />
              {fieldIssue('action.target_status') && <small className='scenario-editor__field-error'>{fieldIssue('action.target_status').message}</small>}
            </label>
          </div>
          <div className='scenario-editor__step-footer'>
            <Button disabled={activeStep !== 3 || !actionValid || isReadOnly} onClick={() => advance(3)} variant='stroke'>Продолжить</Button>
          </div>
        </div>
      </Card>

      <Card className='scenario-editor__section scenario-editor__step' aria-labelledby='export-mapping-heading' data-export-step='4' data-step-state={stepState(4)} tabIndex={-1}>
        <div className='scenario-editor__section-heading scenario-editor__step-heading'>
          {stepMarker(4)}
          <h2 id='export-mapping-heading'>Маппинг полей</h2>
        </div>
        <div className='scenario-editor__section-body'>
          {!isReadOnly && (
            <div className='history-export__copy'>
              {!copyState.open && <Button onClick={openCopy} variant='ghost'>Взять маппинг из сценария…</Button>}
              {copyState.open && (
                <div className='history-export__copy-form'>
                  <label className='scenario-editor__field'>
                    <span>Сценарий</span>
                    <Select aria-label='Сценарий' value={copyState.scenarioId} onChange={event => setCopyState(current => ({ ...current, scenarioId: event.target.value }))}>
                      <option value=''>{copyState.loading ? 'Загружаем сценарии…' : 'Выберите сценарий'}</option>
                      {copyState.scenarios.map(scenario => <option key={scenario.id} value={scenario.id}>{scenario.name}</option>)}
                    </Select>
                  </label>
                  <Button disabled={!copyState.scenarioId || copyState.loading} onClick={copyMapping} variant='stroke'>Взять маппинг</Button>
                  <Button onClick={() => setCopyState(current => ({ ...current, open: false }))} variant='ghost'>Отмена</Button>
                  <small>Копируются сопоставления и тумблеры «Отслеживать». Фильтр и стадия выгрузки остаются свои.</small>
                  {copyState.error && <small className='scenario-editor__field-error'>{copyState.error}</small>}
                </div>
              )}
            </div>
          )}
          <ScenarioMappingBody
            actionStepValid={actionValid}
            activeStep={activeStep}
            addFieldMapping={addFieldMapping}
            crmName={crmName}
            fieldMappingRules={fieldMappingRules}
            fieldOptions={fieldOptions}
            form={form}
            isReadOnly={isReadOnly}
            liveFields={liveFields}
            removeRule={removeRule}
            ruleErrors={ruleErrors}
            ruleWarnings={ruleWarnings}
            slug={slug}
            systemRows={systemRows}
            toggleRuleTracking={toggleRuleTracking}
            toggleSystemTracking={toggleSystemTracking}
            trackingAvailable
            updateRule={updateRule}
            validation={validation}
          />
        </div>
      </Card>

      <Card className='scenario-editor__section scenario-editor__step' aria-labelledby='export-check-heading' data-export-step='5' data-step-state={actionValid ? 'active' : 'inactive'} tabIndex={-1}>
        <div className='scenario-editor__section-heading scenario-editor__step-heading'>
          <span className='scenario-editor__step-number'>5</span>
          <h2 id='export-check-heading'>Проверка и запуск</h2>
        </div>
        <div className='scenario-editor__section-body'>
          {status === 'draft' && <p className='scenario-editor__step-description'>Нажмите «Проверить»: соберём список сделок периода и покажем, сколько будет создано.</p>}
          {status === 'preparing' && <p className='scenario-editor__step-description' role='status'>Собираем список сделок периода… Это может занять несколько минут — страницу можно закрыть.</p>}
          {status === 'failed' && (
            <InlineAlert title='Не удалось собрать список сделок' tone='error'>
              {FAILURE_REASONS[exp.error_reason] || 'Проверьте выгрузку ещё раз. Если ошибка повторится, обратитесь в поддержку.'}
            </InlineAlert>
          )}
          {status === 'ready' && check5 && (
            <>
              <Tiles
                label='Что будет передано'
                items={[
                  { label: 'найдено сделок', value: check5.found },
                  { label: 'уже в Финтабло', value: check5.linked },
                  { label: 'будет создано', value: check5.to_create },
                  { label: `порций по ${check5.batch_size}`, value: check5.batches }
                ]}
              />
              <ul className='history-export__refs' aria-label='Справочники'>
                {referenceLine('employees', 'Ответственные', 'ответственного')}
                {referenceLine('directions', 'Направления', 'направления')}
                <li>
                  <div className='history-export__ref-line'>
                    <span>{buyersText}</span>
                    {unmappedBuyers === 0 || createPartners
                      ? <Status tone='success'>Готово</Status>
                      : <Status tone='warning'>Проверьте</Status>}
                  </div>
                  <div className='history-export__create'>
                    <div className='scenario-editor__tracking'>
                      <button
                        aria-checked={createPartners}
                        aria-label='Создать в Финтабло контрагентов, которых там нет'
                        className='scenario-editor__switch'
                        disabled={busy !== null}
                        onClick={() => changeForm(current => ({ ...current, create_partners: !current.create_partners }))}
                        role='switch'
                        type='button'
                      />
                    </div>
                    <p>
                      Создать в Финтабло контрагентов, которых там нет
                      <small>Только покупатели сделок этой выгрузки. Поиск по названию, как при импорте Финтабло. Созданный контрагент сразу сопоставляется в справочнике. <Link to={ROUTES.REFERENCE(slug, 'counterparties')}>Открыть справочник</Link> — чтобы сопоставить часть вручную.</small>
                    </p>
                  </div>
                </li>
              </ul>
              <div className='history-export__danger'>
                <InlineAlert title='Выгрузку нельзя отменить' tone='error'>
                  {createPartners
                    ? 'Созданные в Финтабло сделки и контрагентов придётся удалять вручную, по одному. Проверьте настройки до того, как нажмёте «Выгрузить».'
                    : 'Созданные в Финтабло сделки придётся удалять вручную, по одной. Проверьте настройки до того, как нажмёте «Выгрузить».'}
                </InlineAlert>
              </div>
              {exp.trial.items.length > 0 && (
                <section className='history-export__trial' aria-labelledby='export-trial-heading'>
                  <h3 id='export-trial-heading'>Пробные сделки</h3>
                  <p className='scenario-editor__step-description'>
                    Откройте сделки в Финтабло по ссылкам и сверьте.
                  </p>
                  {!exp.trial.settings_match && <InlineAlert tone='warning'>Настройки изменились после пробы — отправьте пробу заново.</InlineAlert>}
                  <div className='history-export-list__table-wrap'>
                    <table aria-label='Пробные сделки' className='history-export-list__table'>
                      <thead>
                        <tr><th>Сделка {crmName}</th><th>Сделка Финтабло</th><th>Результат</th><th>Замечания</th></tr>
                      </thead>
                      <tbody>
                        {exp.trial.items.map(item => (
                          <tr key={item.source_id}>
                            <td>{item.source_url
                              ? <a href={item.source_url} rel='noopener noreferrer' target='_blank'>Сделка №{item.source_id}</a>
                              : `Сделка №${item.source_id}`}
                            </td>
                            <td>{item.target_url
                              ? <a href={item.target_url} rel='noopener noreferrer' target='_blank'>№{item.target_id}</a>
                              : item.target_id ? `№${item.target_id}` : '—'}
                            </td>
                            <td>{TRIAL_RESULTS[item.status] || item.status}</td>
                            <td>{(item.notes || []).length ? item.notes.map(note => <small key={note}>{note}</small>) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className='scenario-editor__step-description'>Пробные сделки уже созданы в Финтабло. Если что-то не так — исправьте или удалите их в Финтабло вручную, поменяйте настройки и отправьте пробу заново: она пойдёт на следующих сделках.</p>
                  <div className='history-export__actions'>
                    <Button disabled={!canStart} loading={busy === 'start'} onClick={start}>Всё верно — выгрузить остальные</Button>
                    <Button onClick={() => document.querySelector('[data-export-step="1"]')?.focus()} variant='ghost'>Изменить настройки</Button>
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </Card>

      <div className='scenario-editor__action-bar' role='group' aria-label='Действия выгрузки'>
        <span className='scenario-editor__readiness' data-tone={readiness.tone}>{readiness.text}</span>
        <Button disabled={!canCheck} loading={busy === 'check'} onClick={check} variant='stroke'>Проверить</Button>
        <Button disabled={!canTrial} loading={busy === 'trial'} onClick={trial} variant='stroke'>Отправить 3 пробные сделки</Button>
        <Button disabled={!canStart} loading={busy === 'start'} onClick={start}>Выгрузить</Button>
        {!isReadOnly && <Button disabled={busy !== null || !dirty} loading={busy === 'save'} onClick={save} variant='ghost'>Сохранить черновик</Button>}
        {['draft', 'ready', 'failed'].includes(status) && exp.counts.total === exp.counts.pending && (
          <Button disabled={busy !== null} onClick={remove} variant='ghost'>Удалить</Button>
        )}
      </div>
      {feedback}
      {status === 'ready' && !exp.can_start && exp.trial.items.length === 0 && (
        <p className='scenario-editor__step-description'>«Выгрузить» станет доступна после проверки пробных сделок в Финтабло.</p>
      )}
    </div>
  )
}
